// Minimal fetch wrappers around /api/* proxied through SWA to the linked
// Function App. Authorization travels via a custom header because SWA
// replaces the standard Authorization header with its own HS256 token.

import { isCommunityMarker, parseDataverseItem } from './categorize';
import { compareActivityDesc } from './itemDates';
import type {
  ApiResponse,
  DataverseRow,
  FeedSource,
  ParsedItem,
  UpdatesResult,
} from './types';

export const API_URL = '/api/updates';
export const BOOKMARKS_URL = '/api/bookmarks';
export const MUTES_URL = '/api/mutes';

// Client-side ceiling for /api/* requests. Sits above the server's 10 s × 3
// attempts + 2 × 1.5 s backoff (~35 s) so the server's response wins, but
// well below the browser default (2-5 min) so a stuck connection surfaces
// as a visible error instead of an indefinite spinner.
const API_TIMEOUT_MS = 45_000;

function headersWith(accessToken?: string): Record<string, string> {
  const h: Record<string, string> = { Accept: 'application/json' };
  if (accessToken) h['X-Client-Access-Token'] = accessToken;
  return h;
}

// Upper bound for the detail part of an HTTP error message so a large or
// hostile body cannot flood the dashboard error box.
const ERROR_DETAIL_MAX = 200;

// Builds the Error thrown for a non-OK response: `HTTP <status>: <detail>`.
// Detail prefers the backend's JSON `error` field (e.g. "upstream_error"),
// then resp.statusText (empty over HTTP/2), else is omitted entirely so the
// message never ends in a dangling colon. Never throws: body read or parse
// failures fall through to the statusText fallback.
async function httpError(resp: Response): Promise<Error> {
  let detail = '';
  try {
    const body: unknown = JSON.parse(await resp.text());
    if (body && typeof body === 'object' && 'error' in body) {
      const field = (body as { error: unknown }).error;
      if (typeof field === 'string') detail = field.trim();
    }
  } catch {
    // Non-JSON, empty, or unreadable body: fall back below.
  }
  if (!detail) detail = resp.statusText.trim();
  // Cap by code point, not UTF-16 unit, so a surrogate pair is never split.
  const codePoints = Array.from(detail);
  if (codePoints.length > ERROR_DETAIL_MAX) {
    detail = codePoints.slice(0, ERROR_DETAIL_MAX).join('');
  }
  return new Error(detail ? `HTTP ${resp.status}: ${detail}` : `HTTP ${resp.status}`);
}

// Only http(s) links are rendered; anything else (javascript:, data:, relative,
// unparseable) becomes null so the panel shows plain text instead of a link.
function httpUrlOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  try {
    const u = new URL(value.trim());
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.href : null;
  } catch {
    return null;
  }
}

function nonEmptyStringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function finiteNumberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

// Booleans as-is; "true"/"false" strings (any case, trimmed) mapped, since a
// flow Compose/Select can stringify a Dataverse Yes/No column. Anything else
// counts as enabled: an unreadable flag then still shows the feed's real status
// (and can raise the header warning) instead of hiding a failing feed behind
// "pois käytöstä".
function parseEnabled(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    if (v === 'false') return false;
    if (v === 'true') return true;
  }
  return true;
}

// Parses the read flow's `sources` array (Dataverse clarito_feedsource).
// Untrusted input: a non-array yields [], entries without a non-empty string
// `name` are dropped, and each field falls back to null ('' for category,
// true for enabled, see parseEnabled, 'official' for sourceType) when it has
// the wrong type. sourceType / author accept the read flow's mapped names and
// the raw column names (clarito_sourcetype / clarito_author).
export function parseFeedSources(raw: unknown): FeedSource[] {
  if (!Array.isArray(raw)) return [];
  const out: FeedSource[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
    const r = entry as Record<string, unknown>;
    const name = nonEmptyStringOrNull(r.name);
    if (!name) continue;
    const lastRun = nonEmptyStringOrNull(r.lastRun);
    const lastNewItems = finiteNumberOrNull(r.lastNewItems);
    out.push({
      name,
      feedUrl: httpUrlOrNull(r.feedUrl),
      homeUrl: httpUrlOrNull(r.homeUrl),
      category: typeof r.category === 'string' ? r.category.trim() : '',
      enabled: parseEnabled(r.enabled),
      sortOrder: finiteNumberOrNull(r.sortOrder),
      lastRun: lastRun && !isNaN(new Date(lastRun).getTime()) ? lastRun : null,
      lastStatus: nonEmptyStringOrNull(r.lastStatus),
      lastNewItems: lastNewItems !== null && lastNewItems >= 0 ? lastNewItems : null,
      sourceType:
        isCommunityMarker(r.sourceType) || isCommunityMarker(r.clarito_sourcetype)
          ? 'community'
          : 'official',
      author: nonEmptyStringOrNull(r.author) ?? nonEmptyStringOrNull(r.clarito_author),
    });
  }
  return out;
}

function parseRows(rows: unknown[]): ParsedItem[] {
  return rows
    .filter((r): r is DataverseRow => !!r && typeof r === 'object' && !Array.isArray(r))
    .map((r) => parseDataverseItem(r))
    .filter((item): item is ParsedItem => item !== null);
}

// Official items and community items from one /api/updates body. Rows in
// `community` are community by definition; a row in `value` that is marked
// community (clarito_sourcetype or the summary's `kind`) is moved there too,
// so it can never reach an official view. Duplicate ids keep the first copy,
// and an id present in the community list is dropped from the official items,
// so a row in both `value` and `community` shows only in community.
function splitItems(
  rawItems: unknown[],
  rawCommunity: unknown,
): { items: ParsedItem[]; community: ParsedItem[] } {
  const items: ParsedItem[] = [];
  const community: ParsedItem[] = [];
  for (const item of parseRows(rawItems)) {
    (item.kind === 'community' ? community : items).push(item);
  }
  const forced = Array.isArray(rawCommunity)
    ? parseRows(
        rawCommunity
          .filter((r) => !!r && typeof r === 'object' && !Array.isArray(r))
          .map((r) => ({ ...(r as DataverseRow), clarito_sourcetype: 'community' })),
      )
    : [];
  const seen = new Set<string>();
  const uniqueCommunity = [...forced, ...community].filter((i) => {
    if (seen.has(i.id)) return false;
    seen.add(i.id);
    return true;
  });
  return {
    items: items
      .filter((item) => !seen.has(item.id) && !/^CVE-\d{4}-/i.test(item.title))
      .sort(compareActivityDesc),
    community: uniqueCommunity.sort(compareActivityDesc),
  };
}

export async function fetchUpdates(
  url: string = API_URL,
  accessToken?: string,
): Promise<UpdatesResult> {
  const resp = await fetch(url, {
    method: 'GET',
    headers: headersWith(accessToken),
    signal: AbortSignal.timeout(API_TIMEOUT_MS),
  });
  if (!resp.ok) {
    throw await httpError(resp);
  }
  const json: ApiResponse = await resp.json();
  const rawItems: DataverseRow[] = Array.isArray(json)
    ? json
    : 'value' in json && Array.isArray(json.value)
      ? json.value
      : 'body' in json && Array.isArray(json.body.value)
        ? json.body.value
        : [];
  const envelope = json && typeof json === 'object' && !Array.isArray(json) ? json : null;
  const { items, community } = splitItems(
    rawItems,
    envelope && 'community' in envelope ? envelope.community : undefined,
  );
  const sources = envelope && 'sources' in envelope ? parseFeedSources(envelope.sources) : [];
  return { items, community, sources };
}

export type BookmarkRecord = {
  itemId: string;
  title: string;
  summaryFi: string;
  sourceUrl: string;
  publishedDate: string;
  category: string;
  impact: string;
  sourceFeed?: string;
  subcategory?: string;
  msTag?: string;
  updateType?: string;
  actionNeeded?: boolean;
  // Community snapshot fields; '' or absent on official bookmarks (a missing
  // kind counts as official).
  kind?: string;
  topic?: string;
  postType?: string;
  author?: string;
  bookmarkedAt: string;
};

export async function fetchBookmarks(accessToken: string): Promise<BookmarkRecord[]> {
  const resp = await fetch(BOOKMARKS_URL, {
    method: 'GET',
    headers: headersWith(accessToken),
    signal: AbortSignal.timeout(API_TIMEOUT_MS),
  });
  if (!resp.ok) throw await httpError(resp);
  return (await resp.json()) as BookmarkRecord[];
}

export async function addBookmark(
  accessToken: string,
  item: ParsedItem,
): Promise<void> {
  const resp = await fetch(BOOKMARKS_URL, {
    method: 'POST',
    headers: { ...headersWith(accessToken), 'Content-Type': 'application/json' },
    signal: AbortSignal.timeout(API_TIMEOUT_MS),
    body: JSON.stringify({
      itemId: item.id,
      title: item.title,
      summaryFi: item.summaryFI,
      sourceUrl: item.sourceURL,
      publishedDate: item.publishedDate,
      category: item.category,
      impact: item.impact,
      sourceFeed: item.sourceFeed,
      subcategory: item.subcategory,
      msTag: item.msTag,
      updateType: item.updateType,
      actionNeeded: item.actionNeeded,
      // Community snapshot fields. Undefined on official items, so
      // JSON.stringify leaves them out of the body.
      kind: item.kind,
      topic: item.topic,
      postType: item.postType,
      author: item.author ?? undefined,
    }),
  });
  if (!resp.ok) throw await httpError(resp);
}

export async function removeBookmark(accessToken: string, itemId: string): Promise<void> {
  const resp = await fetch(`${BOOKMARKS_URL}?itemId=${encodeURIComponent(itemId)}`, {
    method: 'DELETE',
    headers: headersWith(accessToken),
    signal: AbortSignal.timeout(API_TIMEOUT_MS),
  });
  if (!resp.ok) throw await httpError(resp);
}

// Per-user "Luettu / hiljennä" marks. Id-only: the SPA already holds the item.
export type MuteRecord = {
  itemId: string;
  mutedAt: string;
};

export async function fetchMutes(accessToken: string): Promise<MuteRecord[]> {
  const resp = await fetch(MUTES_URL, {
    method: 'GET',
    headers: headersWith(accessToken),
    signal: AbortSignal.timeout(API_TIMEOUT_MS),
  });
  if (!resp.ok) throw await httpError(resp);
  return (await resp.json()) as MuteRecord[];
}

export async function addMute(accessToken: string, itemId: string): Promise<void> {
  const resp = await fetch(MUTES_URL, {
    method: 'POST',
    headers: { ...headersWith(accessToken), 'Content-Type': 'application/json' },
    signal: AbortSignal.timeout(API_TIMEOUT_MS),
    body: JSON.stringify({ itemId }),
  });
  if (!resp.ok) throw await httpError(resp);
}

// Unlike bookmarks, the item id is a path segment: DELETE /api/mutes/{itemId}.
export async function removeMute(accessToken: string, itemId: string): Promise<void> {
  const resp = await fetch(`${MUTES_URL}/${encodeURIComponent(itemId)}`, {
    method: 'DELETE',
    headers: headersWith(accessToken),
    signal: AbortSignal.timeout(API_TIMEOUT_MS),
  });
  if (!resp.ok) throw await httpError(resp);
}
